import { Controller } from "@hotwired/stimulus";

// Lazy-loads older pages of the newest-first email thread (Admin::Emails::ThreadEnd::Component)
// as the boundary at the bottom of the thread scrolls into view. Loaded content only ever
// appends below the current scroll position, so there's no scroll position to preserve. In the
// default (original HTML) content mode each page is 25 fixed-height (500px) iframes, tall enough
// that the boundary always lands far below the fold and a fast scroll can't cascade through
// several loads in one gesture. The `loading` guard below only prevents *concurrent* fetches, not
// this kind of chaining -- in clean/plain-text mode, where card height is unconstrained and much
// shorter, a boundary still inside the rootMargin after a page loads will trigger the next load
// immediately (IntersectionObserver fires for a freshly observed element regardless of user
// input), repeating until the thread is fully loaded.
export default class extends Controller {
  static targets = ["boundary", "trigger"];

  connect () {
    this.loading = false;
    // Extends the bottom edge of the root (not the top) by 500px, so the fetch starts while the
    // boundary is still approaching from below -- the user shouldn't need to scroll all the way
    // to it before the next page starts loading.
    this.observer = new IntersectionObserver(this.handleIntersect, { rootMargin: "0px 0px 500px 0px" });

    if (this.hasBoundaryTarget) {
      this.observer.observe(this.boundaryTarget);
    }
  }

  disconnect () {
    this.observer?.disconnect();
  }

  // Stimulus re-fires this for the fresh boundary swapped in by replaceBoundary, so scrolling
  // further keeps loading pages without any manual re-observe call.
  boundaryTargetConnected (element) {
    this.observer?.observe(element);
  }

  handleIntersect = (entries) => {
    if (entries.some(entry => entry.isIntersecting)) {
      this.loadOlder();
    }
  };

  loadOlder () {
    if (this.loading || !this.hasTriggerTarget) return; // no trigger -- the #oldest marker, nothing left to load

    this.loading = true;
    this.setTriggerLoading();

    fetch(this.triggerTarget.href, { headers: { Accept: "text/html" } })
      .then(response => {
        if (!response.ok) throw new Error(`Failed to load older emails: ${response.status}`);

        return response.text();
      })
      .then(html => this.replaceBoundary(html))
      .catch(() => {
        this.loading = false;
        this.setTriggerError();
      });
  }

  // The only two ways out of this state are replaceBoundary (which swaps the whole node) and
  // setTriggerError (which explicitly clears these) -- there's no path back to plain "Load older
  // emails" text on this same node, so this has nothing to undo.
  setTriggerLoading () {
    this.triggerTarget.textContent = "Loading older emails…";
    this.triggerTarget.classList.add("opacity-50", "pointer-events-none");
  }

  // Distinct from the idle "Load older emails" state, so a flaky request reads as a retry-able
  // failure rather than looking identical to a thread that simply hasn't been scrolled to yet.
  // The trigger stays a live click target (and still re-intersects on scroll) either way.
  setTriggerError () {
    this.triggerTarget.textContent = "Couldn't load older emails - retry";
    this.triggerTarget.classList.remove("opacity-50", "pointer-events-none");
  }

  replaceBoundary (html) {
    this.boundaryTarget.outerHTML = html;
    this.loading = false;

    const total = Number(this.boundaryTarget.dataset.total);
    const loaded = Number(this.boundaryTarget.dataset.loaded);

    this.dispatch("loaded", { detail: { total, loaded } });
  }
}
